import React, { useState, useEffect, useRef } from 'react';
import { FiPlus, FiEdit, FiTrash2, FiPackage, FiUpload, FiCalendar } from 'react-icons/fi';
import api from '../../services/api';

const CrossDockForm = ({ formik }) => {
  const [pallets, setPallets] = useState([]);
  const [showAddPallet, setShowAddPallet] = useState(false);
  const [currentPallet, setCurrentPallet] = useState({
    palletLabels: '',
    commodity: '',
    length: '',
    width: '',
    height: '',
    grossWeight: '',
    palletQuantity: 1,
    declaredValue: '',
    notes: ''
  });
  const [warehouses, setWarehouses] = useState([]);
  const [customerWarehouses, setCustomerWarehouses] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  
  // Validation states
  const [labelValidation, setLabelValidation] = useState({
    isValidating: false,
    isValid: true,
    message: ''
  });

  const [isLabelEditable, setIsLabelEditable] = useState(false);
  const isInitialized = useRef(false);
  
  // Form state for cross-dock details
  const [crossDockData, setCrossDockData] = useState({
    expectedArrivalDate: '',
    expectedPickupDate: '',
    sourceType: 'existing', // 'existing' or 'new'
    sourceWarehouseId: '',
    sourceAddress: {
      name: '',
      street_address: '',
      address_line_2: '',
      city: '',
      state_province: '',
      postal_code: '',
      country: 'United States',
      contact_name: '',
      contact_phone: '',
      notes: ''
    },
    destinationWarehouseId: '',
    crossDockDestinationType: 'existing', // 'existing' or 'new'
    crossDockDestinationId: '',
    crossDockDestination: {
      name: '',
      street_address: '',
      address_line_2: '',
      city: '',
      state_province: '',
      postal_code: '',
      country: 'United States',
      contact_name: '',
      contact_phone: '',
      notes: ''
    },
    shippingArrangement: '', // 'customer' or 'warehouse'
    facilityType: '', // 'residential', 'commercial', 'school', 'limited_access'
    siteContactName: '', // Required when warehouse arranges shipping
    siteContactPhone: '', // Required when warehouse arranges shipping
    documentsProvidedNow: false,
    documents: {
      bolNumber: '',
      trackingNumber: '',
      notes: '',
      attachments: [] // Store uploaded files
    },
    deliveryRequirements: {
      liftgateRequired: false,
      whiteGloveService: false,
      insideDelivery: false,
      appointmentRequired: false
    },
    specialInstructions: ''
  });

  // Max crossdock days enforcement
  const [maxCrossdockDays, setMaxCrossdockDays] = useState(null);
  const [maxPickupDate, setMaxPickupDate] = useState(null);

  // Fetch crossdock settings and warehouses on mount
  useEffect(() => {
    fetchWarehouses();
    fetchCustomerWarehouses();

    // Load max crossdock days setting
    api.get('/crossdock/settings')
      .then(res => {
        const days = res.data?.max_crossdock_business_days;
        if (days) {
          setMaxCrossdockDays(parseInt(days));
        }
      })
      .catch((err) => {
        console.error('Failed to load crossdock settings:', err.response?.status, err.message);
      });
  }, []);

  // Extract primitive values for useEffect dependencies
  const arrivalDate = crossDockData.expectedArrivalDate;
  const pickupDate = crossDockData.expectedPickupDate;

  // Recalculate max pickup date when arrival date or max days change
  useEffect(() => {
    if (!arrivalDate || !maxCrossdockDays) {
      setMaxPickupDate(null);
      return;
    }

    api.post('/crossdock/calculate-max-pickup', { arrivalDate })
      .then(res => {
        if (res.data?.maxPickupDate) {
          setMaxPickupDate(res.data.maxPickupDate);

          // If current pickup date exceeds the max, clear it
          if (pickupDate && pickupDate > res.data.maxPickupDate) {
            updateCrossDockData('expectedPickupDate', '');
          }
        }
      })
      .catch(() => {});
  }, [arrivalDate, maxCrossdockDays]);

  // Extract primitive to avoid re-triggering on Formik object rebuilds
  const companyId = formik.values.companyId;

  // Watch for customer selection changes (for admin users)
  useEffect(() => {
    if (companyId) {
      fetchCustomerWarehouses();
      fetchAccessibleWarehouses();
    }
  }, [companyId]);

  // Initialize from formik values if editing (only once)
  useEffect(() => {
    if (!isInitialized.current) {
      if (formik.values.crossdockData) {
        setCrossDockData(formik.values.crossdockData);
      }
      if (formik.values.crossdockPallets && formik.values.crossdockPallets.length > 0) {
        setPallets(formik.values.crossdockPallets);
      }
      isInitialized.current = true;
    }
  }, []);

  // Sync form data to formik (only after initialization)
  useEffect(() => {
    if (isInitialized.current) {
      formik.setFieldValue('crossdockData', crossDockData);
      formik.setFieldValue('crossdockPallets', pallets);
    }
  }, [crossDockData, pallets]);

  useEffect(() => {
    if (crossDockData.destinationWarehouseId || warehouses.length !== 1) return;

    const [onlyWarehouse] = warehouses;
    if (!onlyWarehouse?.id) return;

    setCrossDockData((prev) => (
      prev.destinationWarehouseId
        ? prev
        : { ...prev, destinationWarehouseId: onlyWarehouse.id }
    ));
  }, [crossDockData.destinationWarehouseId, warehouses]);



  const fetchWarehouses = async () => {
    try {
      // If admin user and customer selected, fetch accessible warehouses
      if (formik.values.companyId) {
        const response = await api.get(`/admin/customers/${formik.values.companyId}/accessible-warehouses`);
        const warehouseData = response.data?.accessibleWarehouses || [];
        setWarehouses(Array.isArray(warehouseData) ? warehouseData : []);
      } else {
        // Regular user - fetch their accessible warehouses
        const response = await api.get('/customers/accessible-warehouses');
        const warehouseData = response.data?.data || [];
        setWarehouses(Array.isArray(warehouseData) ? warehouseData : []);
      }
    } catch (error) {
      console.error('Error fetching warehouses:', error);
      setWarehouses([]);
    }
  };

  const fetchCustomerWarehouses = async () => {
    try {
      // If admin user and customer selected, fetch customer's warehouses
      if (formik.values.companyId) {
        const response = await api.get(`/admin/customers/${formik.values.companyId}/warehouse-access`);
        const warehouseData = response.data?.companyWarehouses || [];
        setCustomerWarehouses(Array.isArray(warehouseData) ? warehouseData : []);
      } else {
        // Regular user - fetch their own warehouses
        const response = await api.get('/customers/warehouses');
        const warehouseData = response.data?.data || [];
        setCustomerWarehouses(Array.isArray(warehouseData) ? warehouseData : []);
      }
    } catch (error) {
      console.error('Error fetching customer warehouses:', error);
      setCustomerWarehouses([]);
    }
  };

  const fetchAccessibleWarehouses = async () => {
    try {
      const response = await api.get(`/admin/customers/${formik.values.companyId}/accessible-warehouses`);
      const warehouseData = response.data?.accessibleWarehouses || [];
      setWarehouses(Array.isArray(warehouseData) ? warehouseData : []);
    } catch (error) {
      console.error('Error fetching accessible warehouses:', error);
      setWarehouses([]);
    }
  };

  const generatePalletLabel = async () => {
    try {
      const response = await api.post('/crossdock/generate-label');
      return response.data.palletLabel;
    } catch (error) {
      console.error('Error generating pallet label:', error);
      // Fallback to timestamp-based generation
      const timestamp = Date.now().toString().slice(-6);
      const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
      return `PALLET-${timestamp}${random}`;
    }
  };

  const validatePalletLabels = async (labelsString, quantity, excludeCurrentPallet = false) => {
    try {
      // If labels are empty, we'll auto-generate them
      if (!labelsString.trim()) {
        return { isValid: true, message: 'Labels will be auto-generated', willAutoGenerate: true };
      }

      // Parse comma-separated labels
      const labels = labelsString.split(',').map(label => label.trim()).filter(label => label);
      
      // Validate count matches quantity
      if (labels.length !== quantity) {
        return { 
          isValid: false, 
          message: `Please provide exactly ${quantity} labels (found ${labels.length})` 
        };
      }

      // Check for duplicates within the provided labels
      const duplicates = labels.filter((label, index) => labels.indexOf(label) !== index);
      if (duplicates.length > 0) {
        return { 
          isValid: false, 
          message: `Duplicate labels found: ${duplicates.join(', ')}` 
        };
      }

      // Check for duplicates within current form pallets
      const isDuplicateInForm = pallets.some((pallet, index) => 
        index !== editingIndex && pallet.generatedLabels && 
        pallet.generatedLabels.some(existingLabel => labels.includes(existingLabel))
      );
      
      if (isDuplicateInForm) {
        return {
          isValid: false,
          message: 'One or more labels are already used in another pallet in this request'
        };
      }

      // Server-side uniqueness validation for all labels
      const response = await api.post('/crossdock/validate-labels', { labels });
      return {
        isValid: response.data.isValid,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error validating pallet labels:', error);
      return { isValid: false, message: 'Error validating pallet labels' };
    }
  };



  const handleAddPallet = async () => {
    // Reset pallet form - labels will be empty by default for auto-generation
    setCurrentPallet({
      palletLabels: '', // Empty for auto-generation
      commodity: '',
      length: '',
      width: '',
      height: '',
      grossWeight: '',
      palletQuantity: 1,
      declaredValue: '',
      notes: ''
    });
    setEditingIndex(null);
    setLabelValidation({
      isValidating: false,
      isValid: true,
      message: 'Labels will be auto-generated if left blank'
    });
    setShowAddPallet(true);
  };

  const handleEditPallet = (index) => {
    const pallet = pallets[index];
    setCurrentPallet({
      ...pallet,
      palletLabels: pallet.generatedLabels ? pallet.generatedLabels.join(', ') : pallet.palletLabels || ''
    });
    setEditingIndex(index);
    setLabelValidation({
      isValidating: false,
      isValid: true,
      message: 'Current labels'
    });
    setShowAddPallet(true);
  };

  const handleDeletePallet = (index) => {
    if (window.confirm('Are you sure you want to delete this pallet?')) {
      setPallets(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleSavePallet = async () => {
    // Validate labels if provided
    const validation = await validatePalletLabels(
      currentPallet.palletLabels, 
      currentPallet.palletQuantity, 
      editingIndex !== null
    );
    
    if (!validation.isValid) {
      alert(`Pallet label error: ${validation.message}`);
      return;
    }

    if (!currentPallet.commodity.trim()) {
      alert('Please enter a commodity description');
      return;
    }
    if (!currentPallet.length || !currentPallet.width || !currentPallet.height || !currentPallet.grossWeight) {
      alert('Please fill in all dimensions and weight');
      return;
    }
    if (!currentPallet.declaredValue || parseFloat(currentPallet.declaredValue) <= 0) {
      alert('Please enter an approximate value for this pallet');
      return;
    }
    if (!currentPallet.palletQuantity || currentPallet.palletQuantity < 1) {
      alert('Please enter a valid pallet quantity (1 or more)');
      return;
    }

    // Generate labels if not provided
    let generatedLabels = [];
    if (validation.willAutoGenerate) {
      for (let i = 0; i < currentPallet.palletQuantity; i++) {
        const label = await generatePalletLabel();
        generatedLabels.push(label);
      }
    } else {
      generatedLabels = currentPallet.palletLabels.split(',').map(label => label.trim());
    }

    const palletToSave = {
      ...currentPallet,
      generatedLabels, // Store the actual labels to use
      length: parseFloat(currentPallet.length),
      width: parseFloat(currentPallet.width),
      height: parseFloat(currentPallet.height),
      grossWeight: parseFloat(currentPallet.grossWeight),
      declaredValue: parseFloat(currentPallet.declaredValue) || 0
    };

    if (editingIndex !== null) {
      setPallets(prev => prev.map((pallet, index) => 
        index === editingIndex ? palletToSave : pallet
      ));
    } else {
      setPallets(prev => [...prev, palletToSave]);
    }

    setCurrentPallet({
      palletLabels: '',
      commodity: '',
      length: '',
      width: '',
      height: '',
      grossWeight: '',
      palletQuantity: 1,
      declaredValue: '',
      notes: ''
    });
    setEditingIndex(null);
    setShowAddPallet(false);
  };

  const handleCancel = () => {
    setCurrentPallet({
      palletLabels: '',
      commodity: '',
      length: '',
      width: '',
      height: '',
      grossWeight: '',
      palletQuantity: 1,
      notes: ''
    });
    setEditingIndex(null);
    setShowAddPallet(false);
    setLabelValidation({
      isValidating: false,
      isValid: true,
      message: ''
    });
  };

  const updateCrossDockData = (field, value) => {
    setCrossDockData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const updateNestedField = (parent, field, value) => {
    setCrossDockData(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value
      }
    }));
  };

  return (
    <div className="space-y-6">
      {/* Expected Dates */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Arrival Date *</label>
          <input
            type="date"
            value={crossDockData.expectedArrivalDate}
            onChange={(e) => updateCrossDockData('expectedArrivalDate', e.target.value)}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            min={new Date().toISOString().split('T')[0]}
          />
          {formik.errors.expectedArrivalDate && formik.touched.expectedArrivalDate && (
            <p className="mt-1 text-sm text-red-600">{formik.errors.expectedArrivalDate}</p>
          )}
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700">Pickup Date *</label>
          <input
            type="date"
            value={crossDockData.expectedPickupDate}
            onChange={(e) => updateCrossDockData('expectedPickupDate', e.target.value)}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            min={crossDockData.expectedArrivalDate || new Date().toISOString().split('T')[0]}
            max={maxPickupDate || undefined}
            disabled={!crossDockData.expectedArrivalDate}
          />
          {maxPickupDate && (
            <p className="mt-1 text-xs text-red-500">
              Maximum {maxCrossdockDays} business days from arrival (by {new Date(maxPickupDate + 'T00:00:00').toLocaleDateString()})
            </p>
          )}
          {!crossDockData.expectedArrivalDate && (
            <p className="mt-1 text-xs text-gray-400">Select an arrival date first</p>
          )}
          {formik.errors.expectedPickupDate && formik.touched.expectedPickupDate && (
            <p className="mt-1 text-sm text-red-600">{formik.errors.expectedPickupDate}</p>
          )}
        </div>
      </div>

      {/* Source Address Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Source Address *</label>
        
        {/* Warehouse Selection */}
        <div className="mb-4">
          <select
            value={crossDockData.sourceType === 'existing' ? crossDockData.sourceWarehouseId : 'new'}
            onChange={(e) => {
              if (e.target.value === 'new') {
                updateCrossDockData('sourceType', 'new');
                updateCrossDockData('sourceWarehouseId', '');
              } else if (e.target.value) {
                updateCrossDockData('sourceType', 'existing');
                updateCrossDockData('sourceWarehouseId', e.target.value);
              } else {
                updateCrossDockData('sourceType', 'existing');
                updateCrossDockData('sourceWarehouseId', '');
              }
            }}
            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          >
            <option value="">Select a saved address or add a new one</option>
            {customerWarehouses.map((warehouse) => (
              <option key={warehouse.id} value={warehouse.id}>
                {warehouse.name} - {warehouse.street_address}, {warehouse.city}
              </option>
            ))}
            <option value="new">+ Add new address</option>
          </select>
        </div>

        {crossDockData.sourceType === 'existing' && crossDockData.sourceWarehouseId && (
          <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-md">
            <div className="flex items-center">
              <svg className="h-5 w-5 text-green-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="text-sm font-medium text-green-800">
                  Source: {customerWarehouses.find(w => w.id === crossDockData.sourceWarehouseId)?.name}
                </p>
                <p className="text-xs text-green-700">
                  {customerWarehouses.find(w => w.id === crossDockData.sourceWarehouseId)?.street_address}, {customerWarehouses.find(w => w.id === crossDockData.sourceWarehouseId)?.city}, {customerWarehouses.find(w => w.id === crossDockData.sourceWarehouseId)?.state_province}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Manual Address Entry - Only show when specifically requested */}
        {crossDockData.sourceType === 'new' && (
          <div className="bg-gray-50 p-4 rounded-lg space-y-4">
            <h4 className="text-sm font-medium text-gray-700">Enter New Source Address</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Company Name */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Company/Location Name *</label>
                <input
                  type="text"
                  placeholder="e.g., Main Warehouse, Distribution Center"
                  value={crossDockData.sourceAddress.name}
                  onChange={(e) => updateNestedField('sourceAddress', 'name', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>

              {/* Street Address */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Street Address *</label>
                <input
                  type="text"
                  placeholder="Street address"
                  value={crossDockData.sourceAddress.street_address}
                  onChange={(e) => updateNestedField('sourceAddress', 'street_address', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>

              {/* Address Line 2 */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Address Line 2</label>
                <input
                  type="text"
                  placeholder="Apartment, suite, unit, building, floor, etc."
                  value={crossDockData.sourceAddress.address_line_2}
                  onChange={(e) => updateNestedField('sourceAddress', 'address_line_2', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>

              {/* City */}
              <div>
                <label className="block text-sm font-medium text-gray-700">City *</label>
                <input
                  type="text"
                  placeholder="City"
                  value={crossDockData.sourceAddress.city}
                  onChange={(e) => updateNestedField('sourceAddress', 'city', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>

              {/* State/Province */}
              <div>
                <label className="block text-sm font-medium text-gray-700">State/Province *</label>
                <input
                  type="text"
                  placeholder="State or Province"
                  value={crossDockData.sourceAddress.state_province}
                  onChange={(e) => updateNestedField('sourceAddress', 'state_province', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>

              {/* Postal Code */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Postal Code</label>
                <input
                  type="text"
                  placeholder="Postal/ZIP Code"
                  value={crossDockData.sourceAddress.postal_code}
                  onChange={(e) => updateNestedField('sourceAddress', 'postal_code', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>

              {/* Country */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Country</label>
                <select
                  value={crossDockData.sourceAddress.country}
                  onChange={(e) => updateNestedField('sourceAddress', 'country', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                >
                  <option value="United States">United States</option>
                  <option value="Canada">Canada</option>
                </select>
              </div>

              {/* Contact Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Contact Name</label>
                <input
                  type="text"
                  placeholder="Contact person"
                  value={crossDockData.sourceAddress.contact_name}
                  onChange={(e) => updateNestedField('sourceAddress', 'contact_name', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>

              {/* Contact Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Contact Phone</label>
                <input
                  type="tel"
                  placeholder="Phone number"
                  value={crossDockData.sourceAddress.contact_phone}
                  onChange={(e) => updateNestedField('sourceAddress', 'contact_phone', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Destination Warehouse Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Destination Warehouse *</label>
        
        <select
          value={crossDockData.destinationWarehouseId}
          onChange={(e) => updateCrossDockData('destinationWarehouseId', e.target.value)}
          className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
        >
          <option value="">Select destination warehouse</option>
          {warehouses.map((warehouse) => (
            <option key={warehouse.id} value={warehouse.id}>
              {warehouse.name} - {warehouse.street_address || warehouse.city}, {warehouse.city}
            </option>
          ))}
        </select>
        
        {crossDockData.destinationWarehouseId && (
          <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-md">
            <div className="flex items-center">
              <svg className="h-5 w-5 text-green-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="text-sm font-medium text-green-800">
                  Destination: {warehouses.find(w => w.id === crossDockData.destinationWarehouseId)?.name}
                </p>
                <p className="text-xs text-green-700">
                  {warehouses.find(w => w.id === crossDockData.destinationWarehouseId)?.street_address || warehouses.find(w => w.id === crossDockData.destinationWarehouseId)?.city}, {warehouses.find(w => w.id === crossDockData.destinationWarehouseId)?.city}, {warehouses.find(w => w.id === crossDockData.destinationWarehouseId)?.state_province}
                </p>
              </div>
            </div>
          </div>
        )}
        
        {formik.errors.destinationWarehouseId && formik.touched.destinationWarehouseId && (
          <p className="mt-2 text-sm text-red-600">{formik.errors.destinationWarehouseId}</p>
        )}
      </div>

      {/* Shipping Arrangement */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-4">Who will arrange the outbound shipping? *</label>
        <div className="space-y-3">
          <div className="flex items-center">
            <input
              id="customer-arranges"
              type="radio"
              value="customer"
              checked={crossDockData.shippingArrangement === 'customer'}
              onChange={(e) => updateCrossDockData('shippingArrangement', e.target.value)}
              className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
            />
            <label htmlFor="customer-arranges" className="ml-3 block text-sm font-medium text-gray-700">
              I will arrange shipping
            </label>
          </div>
          <div className="flex items-center">
            <input
              id="warehouse-arranges"
              type="radio"
              value="warehouse"
              checked={crossDockData.shippingArrangement === 'warehouse'}
              onChange={(e) => updateCrossDockData('shippingArrangement', e.target.value)}
              className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
            />
            <label htmlFor="warehouse-arranges" className="ml-3 block text-sm font-medium text-gray-700">
              Please arrange shipping for me
            </label>
          </div>
        </div>
      </div>

      {/* Customer Arranges - Document Upload */}
      {crossDockData.shippingArrangement === 'customer' && (
        <div className="bg-blue-50 p-4 rounded-lg">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Shipping Documents</h4>
          <div className="space-y-3">
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  checked={crossDockData.documentsProvidedNow === true}
                  onChange={() => updateCrossDockData('documentsProvidedNow', true)}
                  className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">Provide BOL/tracking information now</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  checked={crossDockData.documentsProvidedNow === false}
                  onChange={() => updateCrossDockData('documentsProvidedNow', false)}
                  className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">I'll provide documents later</span>
              </label>
            </div>

            {crossDockData.documentsProvidedNow && (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">BOL Number *</label>
                  <input
                    type="text"
                    value={crossDockData.documents.bolNumber}
                    onChange={(e) => updateNestedField('documents', 'bolNumber', e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="Bill of Lading number"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Tracking Number (Optional)</label>
                  <input
                    type="text"
                    value={crossDockData.documents.trackingNumber}
                    onChange={(e) => updateNestedField('documents', 'trackingNumber', e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="Carrier tracking number (if available)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Document Upload *</label>
                  <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                    <div className="space-y-1 text-center">
                      <FiUpload className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="flex text-sm text-gray-600">
                        <label className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500">
                          <span>Upload BOL documents *</span>
                          <input 
                            type="file" 
                            className="sr-only" 
                            multiple 
                            accept=".pdf,.jpg,.jpeg,.png" 
                            onChange={(e) => {
                              const files = Array.from(e.target.files);
                              updateNestedField('documents', 'attachments', files);
                            }}
                            required
                          />
                        </label>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-xs text-gray-500">PDF, PNG, JPG up to 10MB (Required)</p>
                      {crossDockData.documents.attachments && crossDockData.documents.attachments.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-green-600">
                            {crossDockData.documents.attachments.length} file(s) selected
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Warehouse Arranges - Final Destination and Delivery Requirements */}
      {crossDockData.shippingArrangement === 'warehouse' && (
        <div className="space-y-6">
          {/* Final Destination Address */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Final Destination Address *</label>
            
            <div className="mb-4">
              <select
                value={crossDockData.crossDockDestinationType === 'existing' ? crossDockData.crossDockDestinationId : 'new'}
                onChange={(e) => {
                  if (e.target.value === 'new') {
                    updateCrossDockData('crossDockDestinationType', 'new');
                    updateCrossDockData('crossDockDestinationId', '');
                    updateCrossDockData('selectedDestinationDetails', null);
                  } else if (e.target.value) {
                    updateCrossDockData('crossDockDestinationType', 'existing');
                    updateCrossDockData('crossDockDestinationId', e.target.value);
                    // Find and store the selected warehouse details
                    const selectedWarehouse = customerWarehouses.find(w => w.id === e.target.value);
                    updateCrossDockData('selectedDestinationDetails', selectedWarehouse);
                  } else {
                    updateCrossDockData('crossDockDestinationType', 'existing');
                    updateCrossDockData('crossDockDestinationId', '');
                    updateCrossDockData('selectedDestinationDetails', null);
                  }
                }}
                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              >
                <option value="">Select a saved address or add a new one</option>
                {customerWarehouses.map((warehouse) => (
                  <option key={warehouse.id} value={warehouse.id}>
                    {warehouse.name} - {warehouse.street_address}, {warehouse.city}
                  </option>
                ))}
                <option value="new">+ Add new address</option>
              </select>
            </div>

            {crossDockData.crossDockDestinationType === 'existing' && crossDockData.crossDockDestinationId && (
              <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-md">
                <div className="flex items-center">
                  <svg className="h-5 w-5 text-green-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-green-800">
                      Final Destination: {customerWarehouses.find(w => w.id === crossDockData.crossDockDestinationId)?.name}
                    </p>
                    <p className="text-xs text-green-700">
                      {customerWarehouses.find(w => w.id === crossDockData.crossDockDestinationId)?.street_address}, {customerWarehouses.find(w => w.id === crossDockData.crossDockDestinationId)?.city}, {customerWarehouses.find(w => w.id === crossDockData.crossDockDestinationId)?.state_province}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {crossDockData.crossDockDestinationType === 'new' && (
              <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                <h4 className="text-sm font-medium text-gray-700">Enter Final Destination Address</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700">Company/Location Name *</label>
                    <input
                      type="text"
                      placeholder="Final destination company name"
                      value={crossDockData.crossDockDestination.name}
                      onChange={(e) => updateNestedField('crossDockDestination', 'name', e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700">Street Address *</label>
                    <input
                      type="text"
                      placeholder="Street address"
                      value={crossDockData.crossDockDestination.street_address}
                      onChange={(e) => updateNestedField('crossDockDestination', 'street_address', e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700">Address Line 2</label>
                    <input
                      type="text"
                      placeholder="Apartment, suite, unit, building, floor, etc."
                      value={crossDockData.crossDockDestination.address_line_2}
                      onChange={(e) => updateNestedField('crossDockDestination', 'address_line_2', e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">City *</label>
                    <input
                      type="text"
                      placeholder="City"
                      value={crossDockData.crossDockDestination.city}
                      onChange={(e) => updateNestedField('crossDockDestination', 'city', e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">State/Province *</label>
                    <input
                      type="text"
                      placeholder="State or Province"
                      value={crossDockData.crossDockDestination.state_province}
                      onChange={(e) => updateNestedField('crossDockDestination', 'state_province', e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Postal Code</label>
                    <input
                      type="text"
                      placeholder="Postal/ZIP Code"
                      value={crossDockData.crossDockDestination.postal_code}
                      onChange={(e) => updateNestedField('crossDockDestination', 'postal_code', e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Country</label>
                    <select
                      value={crossDockData.crossDockDestination.country}
                      onChange={(e) => updateNestedField('crossDockDestination', 'country', e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    >
                      <option value="United States">United States</option>
                      <option value="Canada">Canada</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Contact Name</label>
                    <input
                      type="text"
                      placeholder="Contact person"
                      value={crossDockData.crossDockDestination.contact_name}
                      onChange={(e) => updateNestedField('crossDockDestination', 'contact_name', e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Contact Phone</label>
                    <input
                      type="tel"
                      placeholder="Phone number"
                      value={crossDockData.crossDockDestination.contact_phone}
                      onChange={(e) => updateNestedField('crossDockDestination', 'contact_phone', e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Site Contact Information */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Site Contact Information *</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Contact Name *</label>
                <input
                  type="text"
                  value={crossDockData.siteContactName}
                  onChange={(e) => updateCrossDockData('siteContactName', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Site contact person"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Contact Phone *</label>
                <input
                  type="tel"
                  value={crossDockData.siteContactPhone}
                  onChange={(e) => updateCrossDockData('siteContactPhone', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Phone number"
                  required
                />
              </div>
            </div>
          </div>

          {/* Facility Type Question */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">What type of facility is the final destination? *</label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="commercial"
                  checked={crossDockData.facilityType === 'commercial'}
                  onChange={(e) => updateCrossDockData('facilityType', e.target.value)}
                  className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">Commercial (business with loading dock)</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="residential"
                  checked={crossDockData.facilityType === 'residential'}
                  onChange={(e) => updateCrossDockData('facilityType', e.target.value)}
                  className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">Residential (home or residential area)</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="school"
                  checked={crossDockData.facilityType === 'school'}
                  onChange={(e) => updateCrossDockData('facilityType', e.target.value)}
                  className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">School or educational facility</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="limited_access"
                  checked={crossDockData.facilityType === 'limited_access'}
                  onChange={(e) => updateCrossDockData('facilityType', e.target.value)}
                  className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">Limited access (restricted delivery hours/access)</span>
              </label>
            </div>
          </div>

          {/* Delivery Requirements */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Delivery Requirements</h4>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={crossDockData.deliveryRequirements.liftgateRequired}
                  onChange={(e) => updateNestedField('deliveryRequirements', 'liftgateRequired', e.target.checked)}
                  className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Liftgate required</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={crossDockData.deliveryRequirements.whiteGloveService}
                  onChange={(e) => updateNestedField('deliveryRequirements', 'whiteGloveService', e.target.checked)}
                  className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">White glove service</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={crossDockData.deliveryRequirements.insideDelivery}
                  onChange={(e) => updateNestedField('deliveryRequirements', 'insideDelivery', e.target.checked)}
                  className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Inside delivery</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={crossDockData.deliveryRequirements.appointmentRequired}
                  onChange={(e) => updateNestedField('deliveryRequirements', 'appointmentRequired', e.target.checked)}
                  className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Appointment required</span>
              </label>
            </div>
            
            {/* Disclaimer */}
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md">
              <p className="text-xs text-amber-800">
                <strong>Note:</strong> If any delivery requirements are needed but not selected above, additional charges will apply and be billed to your account.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Pallets Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Cross-Dock Pallets</h3>
          <button
            type="button"
            onClick={handleAddPallet}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <FiPlus className="h-4 w-4 mr-2" />
            Add Pallet
          </button>
        </div>

        {/* Pallets List */}
        {pallets.length > 0 ? (
          <div className="space-y-3">
            {pallets.map((pallet, index) => (
              <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <FiPackage className="h-4 w-4 text-gray-500" />
                      <span className="font-medium text-gray-900">
                        {pallet.generatedLabels && pallet.generatedLabels.length > 0 
                          ? pallet.generatedLabels.length === 1 
                            ? pallet.generatedLabels[0]
                            : `${pallet.generatedLabels[0]} +${pallet.generatedLabels.length - 1} more`
                          : pallet.palletLabels || 'Labels to be generated'
                        }
                      </span>
                      <span className="text-sm text-gray-500">• {pallet.commodity}</span>
                      {pallet.palletQuantity > 1 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Qty: {pallet.palletQuantity}
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                      <div>
                        <span className="font-medium">Dimensions:</span> {pallet.length}x{pallet.width}x{pallet.height}"
                      </div>
                      <div>
                        <span className="font-medium">Weight:</span> {pallet.grossWeight} lbs
                      </div>
                      <div>
                        <span className="font-medium">Quantity:</span> {pallet.palletQuantity || 1}
                      </div>
                      <div>
                        <span className="font-medium">Value:</span> ${pallet.declaredValue || 0}
                      </div>
                    </div>
                    {pallet.notes && (
                      <div className="mt-2 text-sm text-gray-600">
                        <span className="font-medium">Notes:</span> {pallet.notes}
                      </div>
                    )}
                  </div>
                  <div className="flex space-x-2 ml-4">
                    <button
                      type="button"
                      onClick={() => handleEditPallet(index)}
                      className="text-blue-600 hover:text-blue-800"
                      title="Edit pallet"
                    >
                      <FiEdit className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeletePallet(index)}
                      className="text-red-600 hover:text-red-800"
                      title="Delete pallet"
                    >
                      <FiTrash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <FiPackage className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>No pallets added yet. Click "Add Pallet" to get started.</p>
          </div>
        )}

        {/* Special Instructions */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <label className="block text-sm font-medium text-gray-700 mb-2">Special Instructions</label>
          <textarea
            rows="3"
            value={crossDockData.specialInstructions}
            onChange={(e) => updateCrossDockData('specialInstructions', e.target.value)}
            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            placeholder="Any special handling instructions, notes, or requirements..."
          />
        </div>
      </div>

      {/* Add/Edit Pallet Modal */}
      {showAddPallet && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingIndex !== null ? 'Edit Cross-Dock Pallet' : 'Add Cross-Dock Pallet'}
              </h3>
              
              <div className="space-y-4">
                {/* Commodity */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Commodity Description *</label>
                  <input
                    type="text"
                    value={currentPallet.commodity}
                    onChange={(e) => setCurrentPallet(prev => ({ ...prev, commodity: e.target.value }))}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="e.g., Electronics, Clothing, Food Products"
                  />
                </div>

                {/* Dimensions */}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Pallet Dimensions *</label>
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-500">Length (in)</label>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        value={currentPallet.length}
                        onChange={(e) => setCurrentPallet(prev => ({ ...prev, length: e.target.value }))}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="48"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500">Width (in)</label>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        value={currentPallet.width}
                        onChange={(e) => setCurrentPallet(prev => ({ ...prev, width: e.target.value }))}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="40"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500">Height (in)</label>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        value={currentPallet.height}
                        onChange={(e) => setCurrentPallet(prev => ({ ...prev, height: e.target.value }))}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="72"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500">Weight (lbs)</label>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        value={currentPallet.grossWeight}
                        onChange={(e) => setCurrentPallet(prev => ({ ...prev, grossWeight: e.target.value }))}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="1500"
                      />
                    </div>
                  </div>
                </div>

                {/* Declared Value */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 flex items-center">
                    Approximate Value ($) *
                    <span className="relative group ml-1">
                      <svg className="h-4 w-4 text-gray-400 cursor-help" fill="none" viewBox="0 0 24 24" stroke="currentColor"><circle cx="12" cy="12" r="10" strokeWidth="2"/><path strokeLinecap="round" strokeWidth="2" d="M12 16v-4m0-4h.01"/></svg>
                      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 text-xs text-white bg-gray-800 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                        We need the approximate value for insurance purposes
                      </span>
                    </span>
                  </label>
                  <input
                    type="number"
                    step="1"
                    min="0"
                    value={currentPallet.declaredValue}
                    onChange={(e) => setCurrentPallet(prev => ({ ...prev, declaredValue: e.target.value }))}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="Total approximate value of this pallet"
                  />
                </div>

                {/* Pallet Quantity */}
                <div className="bg-blue-50 p-4 rounded-md">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Pallet Quantity *</label>
                  <div className="flex items-center space-x-4">
                    <input
                      type="number"
                      min="1"
                      max="9999"
                      value={currentPallet.palletQuantity || 1}
                      onChange={(e) => {
                        const newQuantity = parseInt(e.target.value) || 1;
                        setCurrentPallet(prev => ({ ...prev, palletQuantity: newQuantity }));
                        // Re-validate labels when quantity changes
                        if (currentPallet.palletLabels.trim()) {
                          validatePalletLabels(currentPallet.palletLabels, newQuantity).then(validation => {
                            setLabelValidation({
                              isValidating: false,
                              isValid: validation.isValid,
                              message: validation.message
                            });
                          });
                        }
                      }}
                      className="block w-32 border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      placeholder="1"
                    />
                    <div className="text-sm text-gray-600">
                      <p className="font-medium">How many identical pallets?</p>
                      <p className="text-xs">This helps avoid entering the same pallet details multiple times</p>
                    </div>
                  </div>
                </div>

                {/* Pallet Labels */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Pallet Labels</label>
                  <textarea
                    rows="2"
                    value={currentPallet.palletLabels}
                    onChange={async (e) => {
                      const labelsValue = e.target.value;
                      setCurrentPallet(prev => ({ ...prev, palletLabels: labelsValue }));
                      
                      // Validate labels in real-time
                      if (labelsValue.trim() || currentPallet.palletQuantity > 0) {
                        const validation = await validatePalletLabels(labelsValue, currentPallet.palletQuantity);
                        setLabelValidation({
                          isValidating: false,
                          isValid: validation.isValid,
                          message: validation.message
                        });
                      } else {
                        setLabelValidation({
                          isValidating: false,
                          isValid: true,
                          message: 'Labels will be auto-generated if left blank'
                        });
                      }
                    }}
                    className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none sm:text-sm ${
                      labelValidation.isValid 
                        ? 'border-gray-300 focus:border-indigo-500 focus:ring-indigo-500'
                        : 'border-red-300 focus:border-red-500 focus:ring-red-500'
                    }`}
                    placeholder="Please input your pallet labels (comma-separated), pallet labels will be auto-generated if this is left blank"
                  />
                  {labelValidation.message && (
                    <p className={`mt-1 text-xs ${
                      labelValidation.isValid ? 'text-gray-600' : 'text-red-600'
                    }`}>
                      {labelValidation.message}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Enter {currentPallet.palletQuantity} label{currentPallet.palletQuantity > 1 ? 's' : ''} separated by commas, or leave blank to auto-generate
                  </p>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Notes (Optional)</label>
                  <textarea
                    rows="3"
                    value={currentPallet.notes}
                    onChange={(e) => setCurrentPallet(prev => ({ ...prev, notes: e.target.value }))}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="Any special handling instructions or notes..."
                  />
                </div>
              </div>

              {/* Modal Actions */}
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSavePallet}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  {editingIndex !== null ? 'Update Pallet' : 'Add Pallet'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CrossDockForm; 
